import { writable } from 'svelte/store'
import { apiGet } from '../api'
import { loadBrowserState } from './ui'
import { loadUserSettings, readStoredSettings } from './user'

interface Capability {
  name: string
  scope: string
  organizations: string[]
}

interface AuthState {
  isAuthenticated: boolean
  id: string | null
  email: string | null
  name: string | null
  phone: string | null
  capabilities: Record<string, string[]>
  canEnroll: boolean
  isLoading: boolean
}

const signedOut: AuthState = {
  isAuthenticated: false,
  id: null,
  email: null,
  name: null,
  phone: null,
  capabilities: {},
  canEnroll: false,
  isLoading: false
}

export const authState = writable<AuthState>({ ...signedOut, isLoading: true })

const checkAuthStatus = async (): Promise<AuthState> => {
  try {
    const me = (await apiGet('/api/me', { skipAuthRedirect: true })).data
    const held = await apiGet<Capability[]>('/api/me/capability', { skipAuthRedirect: true })
    const capabilities: Record<string, string[]> = {}
    for (const capability of held) {
      capabilities[capability.scope] = capability.organizations
    }
    return {
      isAuthenticated: true,
      id: me.user_id,
      email: me.email,
      name: [me.first_name, me.last_name].filter(Boolean).join(' ') || me.email,
      phone: me.phone || null,
      capabilities,
      canEnroll: (capabilities.enroll || []).length > 0,
      isLoading: false
    }
  } catch (err) {
    return { ...signedOut }
  }
}

export const authInitialized = checkAuthStatus().then(state => {
  authState.set(state)
  const settings = readStoredSettings()
  loadUserSettings(settings)
  if (settings) {
    loadBrowserState(settings)
  }
  return state
})

export const signIn = () => {
  const next = window.location.pathname + window.location.search
  window.location.href = `/api/auth/login?next=${encodeURIComponent(next)}`
}

export const logout = () => {
  authState.set({ ...signedOut })
  loadUserSettings(null)
  window.location.href = '/api/auth/logout'
}

export const clearAuth = () => {
  authState.set({ ...signedOut })
  loadUserSettings(null)
}
